/**
 * /licenses/:id — License manage page (OCP-116).
 *
 * Surfaces:
 *   - License metadata: tier, status, issued/expires, max-instance cap,
 *     features list, air-gapped flag, license-key prefix
 *   - Linked instances: instance_id, instance_url, last_seen, status
 *   - Download `.orbotolic` button (re-mint via POST /download — the
 *     server returns a fresh signature under the active key + the
 *     filename to use for the browser download). Owner / admin only
 *     (OCP-929): the file carries the licence key; other roles see the
 *     read-only notice instead of the button
 *   - Revoke button with confirmation modal (clear copy: "every linked
 *     instance is blocked at next /verify call; cannot be undone")
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDate } from '../lib/format-time';

interface License {
  id: string;
  display_name: string;
  tier: 'solo' | 'team' | 'business' | 'enterprise';
  status: 'active' | 'revoked' | 'expired';
  max_instances: number | null;
  features: string[];
  air_gapped: boolean;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  license_key_prefix: string;
  subscription_id: string | null;
}

interface Instance {
  id: string;
  instance_id: string;
  instance_url: string | null;
  status: 'active' | 'stale' | 'blocked';
  first_seen_at: string;
  last_seen_at: string;
  last_verify_meta: Record<string, unknown> | null;
}

/** OCP-586 - the included mail + AI credentials one instance received (never material). */
interface Entitlement {
  id: string;
  instance_id: string;
  instance_url: string | null;
  mail: { status: 'active' | 'revoked' | 'unavailable'; from: string | null; key_prefix: string | null; delivered_at: string | null };
  ai: { status: 'active' | 'revoked' | 'unavailable'; key_prefix: string | null; delivered_at: string | null };
  last_activated_at: string;
}

/** Badge colours; labels live in `licenses:status.*`, `licenses:detail.instanceStatus.*`, `licenses:tier.*` (OCP-656). */
const STATUS_BADGE: Record<License['status'], string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  revoked: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  expired: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

const INSTANCE_STATUS_BADGE: Record<Instance['status'], string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  stale: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  blocked: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
};

export function LicenseDetailPage() {
  const { t } = useTranslation('licenses');
  const { canManageInfra, canExportData } = usePortalAccess();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [license, setLicense] = useState<License | null>(null);
  const [instances, setInstances] = useState<Instance[] | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<'download' | 'revoke' | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!id) return;
    setError(null);
    try {
      const [licenseRes, instancesRes, entRes] = await Promise.allSettled([
        api<License>(`/v1/portal/licenses/${id}`, { signal }),
        api<{ items: Instance[] }>(`/v1/portal/licenses/${id}/instances`, { signal }),
        api<{ items: Entitlement[] }>(`/v1/portal/licenses/${id}/entitlements`, { signal }),
      ]);
      if (licenseRes.status === 'rejected') throw licenseRes.reason;
      setLicense(licenseRes.value);
      setInstances(instancesRes.status === 'fulfilled' ? instancesRes.value.items : []);
      setEntitlements(entRes.status === 'fulfilled' && Array.isArray(entRes.value?.items) ? entRes.value.items : []);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError && err.status === 404) setError('not_found');
      else if (err instanceof ApiError && err.status === 401) {
        navigate('/login?from=' + encodeURIComponent(`/licenses/${id}`));
      } else setError('load_failed');
    }
  }, [id, navigate]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const onDownload = useCallback(async () => {
    if (!id) return;
    setActionPending('download');
    setError(null);
    try {
      const r = await api<{ jwt: string; filename: string }>(
        `/v1/portal/licenses/${id}/download`,
        { method: 'POST' },
      );
      // Trigger a browser download of the JWT as a .orbotolic file.
      const blob = new Blob([r.jwt], { type: 'application/jwt' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // OCP-929 - the file carries the licence key: owner / admin only. A 403
      // (role lowered while the page was open) gets the explanation.
      if (err instanceof ApiError && err.status === 403) setError('download_forbidden');
      else if (err instanceof ApiError) setError(`download_failed_${err.code ?? err.status}`);
      else setError('download_failed');
    } finally {
      setActionPending(null);
    }
  }, [id]);

  const onRevoke = useCallback(async () => {
    if (!id || revokeReason.trim().length === 0) return;
    setActionPending('revoke');
    try {
      const updated = await api<License>(`/v1/portal/licenses/${id}/revoke`, {
        method: 'POST',
        body: { reason: revokeReason.trim() },
      });
      setLicense(updated);
      setShowRevokeModal(false);
      setRevokeReason('');
      // Refresh the instance list — every row should now be `blocked`.
      void load();
    } catch (err) {
      if (err instanceof ApiError) setError(`revoke_failed_${err.code ?? err.status}`);
      else setError('revoke_failed');
    } finally {
      setActionPending(null);
    }
  }, [id, revokeReason, load]);

  if (error === 'not_found') {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-tight">{t('detail.notFoundTitle')}</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('detail.notFoundBody')}{' '}
          <Link to="/licenses" className="text-brand-700 underline">
            {t('detail.back')}
          </Link>
        </p>
      </div>
    );
  }

  if (!license) {
    return <p className="text-sm text-gray-500">{t('detail.loading')}</p>;
  }

  const badge = { label: t(`status.${license.status}`), cls: STATUS_BADGE[license.status] };
  const isActive = license.status === 'active';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/licenses" className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          ← {t('detail.back')}
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{license.display_name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {t(`tier.${license.tier}`)}
              {license.max_instances === null
                ? t('cap.unlimited')
                : t('cap.upTo', { count: license.max_instances })}
              {license.air_gapped ? t('cap.airGapped') : ''}
            </p>
          </div>
          <span
            data-testid="license-status-badge"
            className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
          >
            {badge.label}
          </span>
        </div>
      </div>

      {error && error !== 'not_found' && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error === 'download_forbidden' ? t('detail.downloadForbidden') : t('detail.actionFailed', { error })}
        </div>
      )}

      {license.status === 'revoked' && (
        <div className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          <strong>{t('detail.revokedLabel')}</strong>
          {license.revoked_reason ? t('detail.revokedReason', { reason: license.revoked_reason }) : ''}
          {license.revoked_at ? t('detail.revokedOn', { date: formatDate(license.revoked_at) }) : ''}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('detail.keyTitle')}
        </h2>
        <p className="mt-2 font-mono text-sm text-gray-700 dark:text-gray-300">
          {license.license_key_prefix}<span className="text-gray-400">…</span>
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {t('detail.keyHintBefore')} <code>.orbotolic</code> {t('detail.keyHintAfter')}
        </p>
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('detail.validity')}
        </h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">{t('detail.issued')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDate(license.issued_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('detail.expires')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {license.expires_at ? formatDate(license.expires_at) : t('detail.perpetual')}
            </dd>
          </div>
        </dl>
      </section>

      {license.features.length > 0 && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('detail.features')}
          </h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {license.features.map((f) => (
              <li
                key={f}
                className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {f}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('detail.linkedInstances')}
        </h2>
        {!instances && <p className="mt-2 text-sm text-gray-500">{t('detail.loadingInstances')}</p>}
        {instances && instances.length === 0 && (
          <p className="mt-2 text-sm text-gray-500" data-testid="no-instances">
            {t('detail.noInstances')}
          </p>
        )}
        {instances && instances.length > 0 && (
          <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-800">
            {instances.map((i) => {
              const ib = { label: t(`detail.instanceStatus.${i.status}`), cls: INSTANCE_STATUS_BADGE[i.status] };
              return (
                <li key={i.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                  <div>
                    <p className="font-mono text-xs text-gray-700 dark:text-gray-300">{i.instance_id}</p>
                    {i.instance_url && (
                      <p className="text-xs text-gray-500">{i.instance_url}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {t('detail.lastSeen', { date: formatDate(i.last_seen_at) })}
                    </p>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${ib.cls}`}>
                    {ib.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5" data-testid="license-entitlements">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('detail.entitlements.title')}
        </h2>
        <p className="mt-2 text-xs text-gray-500">{t('detail.entitlements.hint')}</p>
        {entitlements.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">{t('detail.entitlements.none')}</p>
        )}
        {entitlements.length > 0 && (
          <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-800">
            {entitlements.map((e) => (
              <li key={e.id} className="py-2 text-sm">
                <p className="font-mono text-xs text-gray-700 dark:text-gray-300">{e.instance_id}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('detail.entitlements.mail')}: {t(`detail.entitlements.status.${e.mail.status}`)}
                  {e.mail.from ? ` (${e.mail.from})` : ''}
                  {' · '}
                  {t('detail.entitlements.ai')}: {t(`detail.entitlements.status.${e.ai.status}`)}
                  {e.ai.key_prefix ? ` (${e.ai.key_prefix}…)` : ''}
                </p>
                <p className="text-xs text-gray-500">{t('detail.entitlements.activated', { date: formatDate(e.last_activated_at) })}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <RoleReadOnlyNotice scope="infra" />
      {isActive && <RoleReadOnlyNotice scope="export" />}
      <section className="flex flex-wrap gap-3">
        {isActive && canExportData && (
          <button
            type="button"
            onClick={onDownload}
            disabled={actionPending !== null}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {actionPending === 'download' ? t('detail.building') : t('detail.download')}
          </button>
        )}
        {isActive && canManageInfra && (
          <button
            type="button"
            onClick={() => setShowRevokeModal(true)}
            className="ml-auto rounded border border-red-300 dark:border-red-900/50 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50"
          >
            {t('detail.revoke')}
          </button>
        )}
      </section>

      {showRevokeModal && (
        <RevokeConfirmModal
          reason={revokeReason}
          setReason={setRevokeReason}
          pending={actionPending === 'revoke'}
          onConfirm={onRevoke}
          onClose={() => {
            setShowRevokeModal(false);
            setRevokeReason('');
          }}
        />
      )}
    </div>
  );
}

function RevokeConfirmModal({
  reason,
  setReason,
  pending,
  onConfirm,
  onClose,
}: {
  reason: string;
  setReason: (s: string) => void;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('licenses');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="revoke-modal-title"
      data-testid="revoke-confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-5 shadow-lg">
        <h2 id="revoke-modal-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('revokeModal.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          {t('revokeModal.bodyBefore')}
          <code className="px-1">/verify</code> {t('revokeModal.bodyAfter')}
        </p>
        <label className="mt-4 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('revokeModal.reasonLabel')}</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('revokeModal.reasonPlaceholder')}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            required
          />
        </label>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('revokeModal.keep')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending || reason.trim().length === 0}
            className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {pending ? t('revokeModal.revoking') : t('revokeModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}
